import { useState } from "react";
import { KeyRound } from "lucide-react";
import { authErrorTranslationKey } from "../net/auth-errors.js";
import { api, ApiError } from "../net/api.js";
import { TurnstileWidget, useTurnstilePolicy } from "../ui/Turnstile.js";
import { Button, ErrorNote, Panel } from "../ui/primitives.js";
import { useLocale } from "../i18n.js";

/** A self-contained account-security form used from the profile settings sheet. */
export function SecuritySettings({ hasPassword }: { hasPassword: boolean }) {
  const { t } = useLocale();
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const captchaRequired = useTurnstilePolicy();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null); setMessage(null);
    const needsCaptcha = captchaRequired !== false;
    if (needsCaptcha && captchaToken === null) {
      setError(t(captchaRequired === null ? "captchaUnavailable" : "captchaRequired"));
      return;
    }
    if (password !== confirmation) {
      setError(t("passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.updatePassword(
        { currentPassword: hasPassword ? currentPassword : undefined, password },
        needsCaptcha ? captchaToken ?? undefined : undefined,
      );
      setCurrentPassword(""); setPassword(""); setConfirmation("");
      setMessage(t("passwordSaved"));
    } catch (caught) {
      setError(caught instanceof ApiError
        ? t(authErrorTranslationKey(caught.code, caught.status, false))
        : t("passwordSaveError"));
    } finally {
      setBusy(false);
      setCaptchaToken(null);
      setCaptchaResetKey((key) => key + 1);
    }
  }

  return (
    <Panel className="security-settings">
      <header className="security-settings-heading">
        <span aria-hidden="true"><KeyRound /></span>
        <div>
          <h3>{t(hasPassword ? "changePassword" : "setPassword")}</h3>
          <p>{t("passwordStrength")}</p>
        </div>
      </header>
      <form className="security-form" onSubmit={(event) => void submit(event)}>
        {hasPassword && (
          <label className="field">
            <span className="field-label">{t("currentPassword")}</span>
            <input type="password" autoComplete="current-password" required value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
          </label>
        )}
        <label className="field">
          <span className="field-label">{t("newPassword")}</span>
          <input type="password" autoComplete="new-password" minLength={8} maxLength={72} required value={password} onChange={(event) => setPassword(event.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">{t("confirmPassword")}</span>
          <input type="password" autoComplete="new-password" minLength={8} maxLength={72} required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
        </label>
        <TurnstileWidget enabled={captchaRequired === true} resetKey={captchaResetKey} onToken={setCaptchaToken} />
        <Button type="submit" variant="primary" busy={busy}>{t("save")}</Button>
        {error !== null && <ErrorNote>{error}</ErrorNote>}
        {message !== null && <p className="success-note" role="status">{message}</p>}
      </form>
    </Panel>
  );
}
